// Flattening a scratch block's ProseMirror body down to plain text.
//
// Promote copies text, never nodes: a block that becomes a script row lands
// as one paragraph per line inside the Audio cell, so the shape it had in the
// pane (headings, lists, bold) is deliberately dropped. Keeping this a pure
// function over the stored JSON means promote never needs a live editor for
// the block it is promoting.

import type { ScratchBlockType } from '../../../shared/scratch';

/** An empty block body — one empty paragraph, the shape Tiptap itself makes. */
function emptyScratchBody(): unknown {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

/** A body holding a single line of text, for the quick-add bar. */
export function scratchBodyFromText(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return emptyScratchBody();
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: [{ type: 'text', text: trimmed }] }],
  };
}

interface PMNodeJson {
  type?: string;
  text?: string;
  content?: unknown[];
}

function asNode(value: unknown): PMNodeJson | null {
  if (!value || typeof value !== 'object') return null;
  return value as PMNodeJson;
}

/** Inline text of a node's children, with hard breaks becoming spaces. */
function inlineText(node: PMNodeJson): string {
  const children = Array.isArray(node.content) ? node.content : [];
  let out = '';
  for (const raw of children) {
    const child = asNode(raw);
    if (!child) continue;
    if (child.type === 'text') out += child.text ?? '';
    else if (child.type === 'hardBreak') out += ' ';
    else out += inlineText(child);
  }
  return out;
}

function collect(node: PMNodeJson, lines: string[], listDepth: number) {
  const children = Array.isArray(node.content) ? node.content : [];

  switch (node.type) {
    case 'paragraph':
    case 'heading': {
      const text = inlineText(node).trim();
      if (text) lines.push(listDepth > 0 ? `- ${text}` : text);
      return;
    }
    case 'bulletList':
    case 'orderedList': {
      for (const raw of children) {
        const child = asNode(raw);
        if (child) collect(child, lines, listDepth + 1);
      }
      return;
    }
    case 'listItem': {
      for (const raw of children) {
        const child = asNode(raw);
        if (child) collect(child, lines, listDepth);
      }
      return;
    }
    default: {
      // doc, blockquote, anything unrecognised: walk through it.
      for (const raw of children) {
        const child = asNode(raw);
        if (child) collect(child, lines, listDepth);
      }
    }
  }
}

/**
 * Flatten a block body to plain text: one line per block node, `- ` in front
 * of list items (at any nesting depth), blank lines dropped so promoting a
 * block never produces empty script rows or paragraphs.
 */
export function scratchBodyToText(body: unknown): string {
  const root = asNode(body);
  if (!root) return '';
  const lines: string[] = [];
  collect(root, lines, 0);
  return lines.join('\n');
}

/** First line of a block, for menus and aria labels. Never longer than `max`. */
export function scratchBodySummary(body: unknown, type: ScratchBlockType, max = 60): string {
  const text = scratchBodyToText(body).split('\n')[0] ?? '';
  if (!text) return `Empty ${type} block`;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
